<script setup>
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Sheet,
  SheetContent,
  SheetClose,
  SheetTrigger,
} from '@/components/ui/sheet'
import { Menu } from 'lucide-vue-next'
import { useAuthStore } from '@/stores/auth.js'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import UmisisLanguageSwitcher from '@/components/umisis/navigation/UmisisLanguageSwitcher.vue'
import { storeToRefs } from 'pinia'
import { useTopMenuStore } from '@/stores/topMenuStore.js'
import NotificationBell from '@/components/umisis/notifications/NotificationBell.vue'

const { t } = useT()
const router = useRouter()
// You can make these menu items dynamic with props later

const props = defineProps({
  menuItems: {
    type: Array,
    default: () => [],
  },
  isInContainer: {
    type: Boolean,
    default:false,
  }
})

const menuStore = useTopMenuStore()
const { rootItemsOnly: topMenuItems } = storeToRefs(menuStore)

// get the logged in user from the auth store
const authStore = useAuthStore()
const currentUser = ref(authStore.currentUser)

// Handlers for dropdown actions
const loadingLogout = ref(false)
const onLogout = async () => {
  loadingLogout.value = true
  await useAuthStore().logout()
  loadingLogout.value = false
  // redirect the user to the login page using the vue router
  await router.push({ name: 'Login' })
}
</script>

<template>
  <nav class="border-b bg-background">
    <div :class="['flex h-16 items-center justify-between px-6', {'container mx-auto': isInContainer}]">
      <router-link :to="{name: 'Home'}">
        <img src="/umisis.core.svg" alt="Umisis Core Logo" class="h-10">
      </router-link>

      <div class="hidden md:flex items-center gap-2">
        <router-link v-for="(item, index) in topMenuItems" :key="`navigation-item-${index}`" v-slot="{ navigate, isActive }" :to="item.children && item.children[0] && item.children[0].to? item.children[0].to: null" custom>
          <Button :variant="isActive ? 'default' : 'ghost'" @click="navigate">
            {{ $t(item.title) }}
          </Button>
        </router-link>
      </div>

      <div class="hidden md:block">
        <NotificationBell class="mt-1" />
        <UmisisLanguageSwitcher />
        <DropdownMenu>
          <DropdownMenuTrigger as-child>
            <Button variant="ghost" class="relative h-10 w-10 rounded-full">
              <Avatar v-if="currentUser" class="h-10 w-10">
                <AvatarImage v-if="currentUser.profile_photo_url" :src="currentUser.profile_photo_url" :alt="currentUser.initials" />
                <AvatarFallback>{{ currentUser.initials }}</AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent class="w-56" align="end">
            <DropdownMenuLabel>
              <div class="flex flex-col space-y-1">
                <p class="text-sm font-medium leading-none">
                  {{ currentUser.name }}
                </p>
                <p class="text-xs leading-none text-muted-foreground">
                  {{ currentUser.email }}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem as-child>
              <router-link :to="{name: 'User'}">
                {{ t('userMenu.profile') }}
              </router-link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem class="text-destructive hover:text-destructive" @click="onLogout">
              {{ t('userMenu.logout') }}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div class="md:hidden">
        <Sheet>
          <SheetTrigger as-child>
            <Button variant="ghost" size="icon">
              <Menu class="h-6 w-6" />
              <span class="sr-only">Toggle menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="right">
            <div class="border-b pb-4">
              <span class="font-bold text-lg">MyLogo</span>
            </div>

            <div class="flex flex-col gap-2 py-4">
              <SheetClose v-for="item in topMenuItems" :key="item.name" as-child>
                <Button variant="ghost" as-child class="justify-start">
                  <router-link :to="item.to">
                    {{ $t(item.title) }}
                  </router-link>
                </Button>
              </SheetClose>
            </div>

            <div class="border-t pt-4">
              <div class="flex items-center gap-3 px-3 py-2">
                <Avatar>
                  <AvatarImage v-if="currentUser.profile_photo_url" :src="currentUser.profile_photo_url" :alt="currentUser.initials" />
                  <AvatarFallback>{{ currentUser.initials }}</AvatarFallback>
                </Avatar>
                <div>
                  <p class="text-sm font-medium leading-none">
                    {{ currentUser.name }}
                  </p>
                  <p class="text-xs leading-none text-muted-foreground">
                    {{ currentUser.email }}
                  </p>
                </div>
              </div>
              <SheetClose as-child>
                <Button as-child variant="ghost" class="w-full justify-start mt-2">
                  <router-link :to="{name: 'User'}">
                    Profile
                  </router-link>
                </Button>
              </SheetClose>
              <SheetClose as-child>
                <Button variant="ghost" class="w-full justify-start">
                  Settings
                </Button>
              </SheetClose>
              <SheetClose as-child>
                <Button :loading="loadingLogout" variant="ghost" class="w-full justify-start text-destructive hover:text-destructive" @click="onLogout">
                  Log out
                </Button>
              </SheetClose>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  </nav>
</template>
